import { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import JSZip from 'jszip';
import { themeMakerConfig, FieldConfig, GetUserData } from '../..';

export function useThemeMakerAPI(onClose: () => void) {
  const modalRef = useRef<HTMLDivElement>(null);
  const muiTheme = useTheme();

  const [themes, setThemes] = useState<Record<string, any>[]>([]);
  const [activeThemeIndex, setActiveThemeIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const defaults: Record<string, any> = {};
    themeMakerConfig.forEach((section) => {
      defaults[section.id] = section.defaultValues;
    });
    defaults.css = { raw: '' };

    setThemes([defaults]);
  }, []);

  useEffect(() => {
    async function fetchUser() {
      const userData = await GetUserData();
      if ('username' in userData) {
        setThemes((prev) => {
          const updated = [...prev];
          if (!updated[activeThemeIndex]) return prev;
          updated[activeThemeIndex] = {
            ...updated[activeThemeIndex],
            registration: {
              ...updated[activeThemeIndex].registration,
              creator: userData.username,
            },
          };
          return updated;
        });
      }
    }
    fetchUser();
  }, [activeThemeIndex]);

  const handleChange = (sectionId: string, field: FieldConfig, value: any) => {
    setThemes((prev) => {
      const updated = [...prev];
      const theme = { ...updated[activeThemeIndex] };

      const keys = field.key.split('.');
      let obj = theme[sectionId];
      for (let i = 0; i < keys.length - 1; i++) {
        obj = obj[keys[i]] ?? (obj[keys[i]] = {});
      }
      obj[keys[keys.length - 1]] = value;

      updated[activeThemeIndex] = theme;
      return updated;
    });
  };

  const handleCssChange = (value: string) => {
    setThemes((prev) => {
      const updated = [...prev];
      updated[activeThemeIndex] = {
        ...updated[activeThemeIndex],
        css: { raw: value },
      };
      return updated;
    });
  };

  const addTheme = () => {
    const defaults: Record<string, any> = {};
    themeMakerConfig.forEach((section) => {
      defaults[section.id] = section.defaultValues;
    });
    defaults.css = { raw: '' };

    setThemes((prev) => [...prev, defaults]);
    setActiveThemeIndex((prev) => prev + 1);
  };

  const removeTheme = (index: number) => {
    setThemes((prev) => prev.filter((_, i) => i !== index));
    setActiveThemeIndex((prevIndex) => Math.max(0, prevIndex - 1));
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const zip = new JSZip();

      themes.forEach((themeObj, idx) => {
        const { registration, css, ...themeConfig } = themeObj;

        const themeName = registration?.name || `theme-${idx + 1}`;
        const folder = zip.folder(themeName);

        // prepare files
        const themeJson = JSON.stringify(themeConfig, null, 2);
        const cssFile = css?.raw?.trim() || '';

        // registration.json gets paths to theme.json + css
        const registrationObj = {
          id: registration?.id || `theme-${idx + 1}`,
          name: registration?.name || themeName,
          description: registration?.description || '',
          creator: registration?.creator || 'System',
          type: 'theme',
          themePath: ['theme.json'],
          include: cssFile ? ['theme.css'] : [],
        };

        // add files into zip
        folder?.file(
          'registration.json',
          JSON.stringify(registrationObj, null, 2)
        );
        folder?.file('theme.json', themeJson);
        if (cssFile) folder?.file('theme.css', cssFile);
      });

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `themes.zip`;
      a.click();
      URL.revokeObjectURL(url);

      onClose();
    } catch (e) {
      console.error('Save theme failed', e);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    modalRef,
    muiTheme,
    themes,
    activeThemeIndex,
    setActiveThemeIndex,
    formState: themes[activeThemeIndex] || {},
    submitting,
    handleChange,
    handleCssChange,
    handleSave,
    addTheme,
    removeTheme,
  };
}
